import { Icon, Pressable } from '@rbilabs/universal-components';
import { usePaymentContext } from 'features/payment';
import { IPaymentMethod } from 'features/payment/types';
import { shouldReVaultPaymentMethod } from 'features/payment/utils';
import { useIntl } from 'react-intl';
import { StyleSheet, Text, View } from 'react-native';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { useLocale } from 'state/intl';
import { theme } from 'styles/configure-theme';
import { primitive } from 'styles/constants/primitives';
import { Region } from 'utils/environment/types';

import { PaymentMethodDetails } from '../payment-method-details';
import { PaymentMethodIcon } from '../payment-method-icon';

import { styles as commonStyles } from './styles';
import { SelectionType } from './types';

type SelectorOptionProps = {
  option: IPaymentMethod;
  bordered?: boolean;
  selectedMethod: IPaymentMethod | undefined;
  onSelect: (type: SelectionType, id: string) => void;
  onUpdatePaymentMethod?: (id: string) => void;
};

export function SelectorOption({
  option,
  selectedMethod,
  onSelect,
  bordered,
}: SelectorOptionProps) {
  const { formatMessage } = useIntl();
  const { region } = useLocale();
  const isCARegion = region === Region.CA;
  const { isOrbital } = usePaymentContext();

  if (option.prepaid && isCARegion) {
    return null;
  }

  const accountIdentifier = option.accountIdentifier ?? option.fdAccountId ?? '';
  const isSelectedMethod =
    accountIdentifier === selectedMethod?.fdAccountId ||
    accountIdentifier === selectedMethod?.accountIdentifier;
  const { isInvalid = false, isExpired = false } = shouldReVaultPaymentMethod(option, isOrbital);
  const isRevaultRequired = isInvalid || isExpired;

  return (
    <Pressable
      testID={`method-${option.fdAccountId}`}
      style={[
        commonStyles.option,
        {
          backgroundColor: isRevaultRequired ? primitive.bk.$errorLight : undefined,
          borderBottomWidth: bordered ? 1 : 0,
        },
      ]}
      onPress={() => {
        if (isRevaultRequired) {
          return;
        }
        onSelect('selection', accountIdentifier);
      }}
    >
      <View style={styles.optionContent}>
        <View style={[commonStyles.row, { gap: primitive.spacing.$2 }]}>
          <PaymentMethodIcon paymentMethod={option} size="sm" bordered />
          <PaymentMethodDetails paymentMethod={option} displayFor="selectorOption" />
        </View>
        {isRevaultRequired && (
          <View style={[commonStyles.row, { gap: primitive.spacing.$1 }]}>
            <Icon variant="warning" size="xs" />
            <Text style={styles.expiredCardText}>{formatMessage({ id: 'expiredCard' })}</Text>
          </View>
        )}
      </View>
      <View style={commonStyles.separator} />

      {isSelectedMethod && !isRevaultRequired && (
        <Icon variant="check" title="check" color={theme.token('icon-default')} size={5} />
      )}

      {isRevaultRequired && (
        <ActionButton
          onPress={() => onSelect('update', accountIdentifier)}
          variant={ActionButtonVariants.OUTLINE}
          size={ActionButtonSizes.SMALL}
        >
          {formatMessage({ id: 'update' })}
        </ActionButton>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  optionContent: {
    flexDirection: 'column',
  },
  expiredCardText: {
    fontFamily: primitive.fontFamily.$body,
    color: primitive.bk.$bbqBrown,
    fontSize: 12,
  },
});
